import { useEffect, useState } from 'preact/hooks';

export interface SessionTimerProps {
  readonly expiresAt: string | null;
}

export function SessionTimer({ expiresAt }: SessionTimerProps) {
  const [remaining, setRemaining] = useState(() => remainingSeconds(expiresAt));

  useEffect(() => {
    setRemaining(remainingSeconds(expiresAt));
    if (expiresAt === null) return;
    const interval = window.setInterval(() => setRemaining(remainingSeconds(expiresAt)), 1_000);
    return () => window.clearInterval(interval);
  }, [expiresAt]);

  if (expiresAt === null) {
    return (
      <div class="session-timer session-timer--untimed" role="status">
        <span class="session-timer__orb" aria-hidden="true">
          <span />
        </span>
        <span class="session-timer__copy">
          <span>Session</span>
          <strong>No fixed time limit</strong>
        </span>
      </div>
    );
  }
  if (remaining === null) return null;
  const urgent = remaining <= 60;
  return (
    <div
      class={`session-timer ${urgent ? 'is-urgent' : ''}`}
      role="timer"
      aria-live="off"
      aria-label={`Screen sharing ends in ${formatDurationWords(remaining)}`}
    >
      <span class="session-timer__orb" aria-hidden="true">
        <span />
      </span>
      <span class="session-timer__copy">
        <span>Ends in</span>
        <strong>{formatClock(remaining)}</strong>
      </span>
    </div>
  );
}

export function remainingSeconds(expiresAt: string | null, now = Date.now()): number | null {
  if (expiresAt === null) return null;
  const timestamp = Date.parse(expiresAt);
  if (!Number.isFinite(timestamp)) return null;
  return Math.max(0, Math.ceil((timestamp - now) / 1_000));
}

export function formatClock(totalSeconds: number): string {
  const bounded = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(bounded / 3_600);
  const minutes = Math.floor((bounded % 3_600) / 60);
  const seconds = bounded % 60;
  return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
}

function formatDurationWords(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  return [hours ? `${hours} hours` : '', minutes ? `${minutes} minutes` : '', `${seconds} seconds`]
    .filter(Boolean)
    .join(' ');
}
